import {
  fetchBaseQuery,
  type BaseQueryFn,
  type QueryReturnValue,
  type FetchArgs,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query";

export const SERIALIZE_BY_HEADER = "X-Serialize-By";
const SERIALIZE_DEBOUNCE_MS_HEADER = "X-Serialize-Debounce-Ms";
const PRUNE_BY_HEADER = "X-Prune-By";

type SerializeKey = string;
interface QueueEntry {
  pendingCount: number;
  tail: Promise<void>;

  abortedResult: QueryReturnValue<
    unknown,
    FetchBaseQueryError,
    FetchBaseQueryMeta
  > | null;

  debounceTimerId: number | null;
  debouncedRequests: {
    rawArgs: FetchArgs;
    pruneBy: string | null;
    api: Parameters<BaseQueryFn>[1];
    extraOptions: Parameters<BaseQueryFn>[2];
    resolve: (
      value: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>,
    ) => void;
    reject: (reason: unknown) => void;
  }[];
}

const serializeQueues = new Map<SerializeKey, QueueEntry>();

type InternalBaseQuery = ReturnType<typeof fetchBaseQuery>;

type DebouncedRequest = QueueEntry["debouncedRequests"][number];

async function drainQueue(params: {
  queue: QueueEntry;
  baseQuery: InternalBaseQuery;
}): Promise<void> {
  const { queue, baseQuery } = params;

  // Drain the queue one pruned run at a time.
  // Note: this can pick up newly enqueued requests while draining, which will mean they don't get their 'own' debounce
  // but thats fine as waiting on the prior result delays them anyways.
  let run = shiftNextPrunedRun(queue);
  while (run != null && queue.abortedResult == null) {
    let res: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>;
    try {
      res = await baseQuery(
        run.requestToExecute.rawArgs,
        run.requestToExecute.api,
        run.requestToExecute.extraOptions,
      );
    } catch (error: unknown) {
      // Non-network errors in our wrapper should reject the promises.
      run.requests.forEach((r) => {
        r.reject(error);
      });
      queue.pendingCount -= run.requests.length;

      const pending = queue.debouncedRequests.splice(
        0,
        queue.debouncedRequests.length,
      );
      pending.forEach((r) => {
        r.reject(error);
      });
      queue.pendingCount -= pending.length;

      maybeClearAbortedState(queue);
      return;
    }

    run.requests.forEach((r) => {
      r.resolve(res);
    });
    queue.pendingCount -= run.requests.length;

    if ("error" in res) {
      abortQueue({ queue, errorResult: res });
      break;
    }
    run = shiftNextPrunedRun(queue);
  }

  // abortQueue already flushed debouncedRequests and decremented pendingCount.
  maybeClearAbortedState(queue);
}

function shiftNextPrunedRun(queue: QueueEntry): {
  requests: DebouncedRequest[];
  requestToExecute: DebouncedRequest;
} | null {
  const first = queue.debouncedRequests.shift();
  if (!first) return null;

  // Only prune when a pruneBy key is provided.
  if (first.pruneBy == null) {
    return { requests: [first], requestToExecute: first };
  }

  const requests: DebouncedRequest[] = [first];
  while (queue.debouncedRequests.length > 0) {
    const peek = queue.debouncedRequests[0];
    if (!peek) break;
    if (peek.pruneBy == null) break;
    if (peek.pruneBy !== first.pruneBy) break;
    const shifted = queue.debouncedRequests.shift();
    if (!shifted) break;
    requests.push(shifted);
  }

  const requestToExecute = requests.at(-1);
  if (!requestToExecute) return null;
  return { requests, requestToExecute };
}

function abortQueue(params: {
  queue: QueueEntry;
  errorResult: QueryReturnValue<
    unknown,
    FetchBaseQueryError,
    FetchBaseQueryMeta
  >;
}): void {
  const { queue, errorResult } = params;

  if (queue.abortedResult == null) {
    queue.abortedResult = errorResult;
  }

  if (queue.debounceTimerId != null) {
    window.clearTimeout(queue.debounceTimerId);
    queue.debounceTimerId = null;
  }

  const pendingDebounced = queue.debouncedRequests.splice(
    0,
    queue.debouncedRequests.length,
  );
  if (pendingDebounced.length > 0) {
    pendingDebounced.forEach((r) => {
      r.resolve(errorResult);
    });
    queue.pendingCount -= pendingDebounced.length;
  }
}

export function createSerializedFetchBaseQuery(
  args: Parameters<typeof fetchBaseQuery>[0],
): BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
> {
  const baseQuery = fetchBaseQuery(args);

  return async (rawArgs, api, extraOptions) => {
    // If the rawArgs is a string, it's not a fetchArgs object, so we can just return the result of the baseQuery.
    if (typeof rawArgs === "string") {
      return baseQuery(rawArgs, api, extraOptions);
    }

    const serializeBy = readHeaderValue(rawArgs.headers, SERIALIZE_BY_HEADER);
    if (!serializeBy) {
      return baseQuery(rawArgs, api, extraOptions);
    }

    // Queue is keyed by `X-Serialize-By`
    const queue = getOrCreateQueue(serializeBy);
    if (queue.abortedResult != null) {
      // Once aborted, short-circuit new requests for this key with the same error.
      // The key becomes "re-enabled" automatically once the queue is idle again.
      return queue.abortedResult;
    }

    // counts both queued and in-flight
    queue.pendingCount += 1;

    const debounceMs = (() => {
      const raw = readHeaderValue(
        rawArgs.headers,
        SERIALIZE_DEBOUNCE_MS_HEADER,
      );
      if (raw == null) return 0;
      const ms = Number(raw);
      if (!Number.isFinite(ms) || ms < 0) return 0;
      return ms;
    })();

    return await new Promise((resolve, reject) => {
      queue.debouncedRequests.push({
        rawArgs,
        pruneBy: readHeaderValue(rawArgs.headers, PRUNE_BY_HEADER),
        api,
        extraOptions,
        resolve,
        reject,
      });

      // restart debounce:
      if (queue.debounceTimerId != null) {
        window.clearTimeout(queue.debounceTimerId);
      }
      queue.debounceTimerId = window.setTimeout(() => {
        queue.debounceTimerId = null;

        // queue.tail promise used as a serialization primitive by chaining .then onto it
        queue.tail = queue.tail
          .catch(() => {
            // keep chain alive
          })
          .then(async () => {
            await drainQueue({ queue, baseQuery });
          })
          .catch((error: unknown) => {
            // Catch-all for unexpected wrapper failures: reject everything pending.
            const pending = queue.debouncedRequests.splice(
              0,
              queue.debouncedRequests.length,
            );
            pending.forEach((r) => {
              r.reject(error);
            });
            queue.pendingCount -= pending.length;
            maybeClearAbortedState(queue);
          });
      }, debounceMs);
    });
  };
}

function getOrCreateQueue(key: SerializeKey): QueueEntry {
  const existing = serializeQueues.get(key);
  if (existing) return existing;

  const created: QueueEntry = {
    pendingCount: 0,
    tail: Promise.resolve(),
    abortedResult: null,
    debounceTimerId: null,
    debouncedRequests: [],
  };
  serializeQueues.set(key, created);
  return created;
}

function readHeaderValue(
  headers: FetchArgs["headers"],
  headerKey: string,
): string | null {
  if (!headers) return null;

  if (headers instanceof Headers) {
    return headers.get(headerKey);
  }

  if (Array.isArray(headers)) {
    const headerKeyLower = headerKey.toLowerCase();
    const match = headers.find(
      (entry) => entry[0]?.toLowerCase() === headerKeyLower,
    );
    return match?.[1] ?? null;
  }

  const record = headers;
  return record[headerKey] ?? null;
}

function withHeader(
  headers: HeadersInit | undefined,
  headerKey: string,
  headerValue: string,
): HeadersInit {
  if (!headers) return { [headerKey]: headerValue };

  if (headers instanceof Headers) {
    headers.set(headerKey, headerValue);
    return headers;
  }

  if (Array.isArray(headers)) {
    return [...headers, [headerKey, headerValue]] as const;
  }

  return { ...headers, [headerKey]: headerValue };
}

export { withHeader };

function maybeClearAbortedState(queue: QueueEntry): void {
  if (queue.pendingCount !== 0) return;
  if (queue.abortedResult == null) return;

  queue.abortedResult = null;
}
